import React, { useState, useEffect } from 'react';
import { Table, Button, message, Tag, Input } from 'antd';
import { EyeOutlined, SearchOutlined, TeamOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

interface Member {
  id: number;
  user_name: string;
  contact_name: string;
  contact_phone: string;
  company_name: string;
  credit_code: string;
  auth_status: number;
  status_flag: number;
  create_time: string;
}

export default function Members() {
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const resp = await fetch('/api/admin/members', {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setMembers(resp.data || []);
      } else {
        message.error(resp?.error || '获取成员列表失败');
      }
    } catch (err) {
      message.error('获取成员列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 过滤数据
  const filteredMembers = members.filter(m => 
    m.user_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    m.contact_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    m.company_name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    m.contact_phone?.includes(searchKeyword)
  );

  const columns: ColumnsType<Member> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (id) => <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>#{id}</span>
    },
    {
      title: '账号',
      dataIndex: 'user_name',
      key: 'user_name',
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '联系人',
      dataIndex: 'contact_name',
      key: 'contact_name',
    },
    {
      title: '联系电话',
      dataIndex: 'contact_phone',
      key: 'contact_phone',
      render: (text) => <span style={{ fontFamily: 'monospace' }}>{text}</span>
    },
    {
      title: '公司名称',
      dataIndex: 'company_name',
      key: 'company_name',
      ellipsis: true,
    },
    {
      title: '纳税人识别号',
      dataIndex: 'credit_code',
      key: 'credit_code',
      render: (text) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</span>
    },
    {
      title: '审核状态',
      dataIndex: 'auth_status',
      key: 'auth_status',
      render: (status: number) => {
        const statusMap: Record<number, { text: string; color: string }> = {
          0: { text: '未审核', color: 'warning' },
          1: { text: '审核通过', color: 'success' },
          2: { text: '审核不通过', color: 'error' },
        };
        const s = statusMap[status] || statusMap[0];
        return <Tag color={s.color} style={{ borderRadius: 12 }}>{s.text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      render: (text) => <span style={{ color: '#6b7280', fontSize: 13 }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => message.info('查看成员详情功能待实现')}
          style={{ color: '#3b82f6' }}
        >
          查看
        </Button>
      ),
    },
  ];

  return (
    <div className="admin-page">
      {/* 页面头部 */}
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <TeamOutlined />
          </div>
          <div>
            <h2>成员管理</h2>
            <p>管理平台成员信息</p>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: 12 }}>
          <Input
            placeholder="搜索成员..."
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchMembers}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </div>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{filteredMembers.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={filteredMembers}
          loading={loading}
          rowKey="id"
          className="admin-table"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>
    </div>
  );
}
